import * as Sentry from '@sentry/react';
import {
  Account_Create,
  Account_Invite,
  AccountInviteCompanyType,
  AccountType,
  CompanyShare_Create,
  CompanyShareableType,
  CompanyType,
  Contact_Create,
  ModelError_Response,
  postV1CompaniesCompanyIdAccounts,
  postV1CompaniesCompanyIdAccountsInvite,
  postV1CompaniesCompanyIdCompanyShares,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';

import { AutocompleteBooleanOptionValue } from '~components/FormFields/CommonAutocompletes/AutocompleteBoolean';
import { Account } from '~hooks/useAccount/models';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { InviteType } from '~types/InviteTypes';

import { ConnectedAccountContactPanelHandler } from '../ConnectedAccountContactPanel/ConnectedAccountContactPanel';
import { NotesPanelHandler } from '../NotesPanel/NotesPanel';
import { AccountConfigurationPanelHandler } from './AccountConfigurationPanel/AccountConfigurationPanel';
import { AccountContactPanelHandler } from './AccountContactPanel/AccountContactPanel';
import { AccountDetailsPanelHandler } from './AccountDetailsPanel/AccountDetailsPanel';

// Maps the (non-super) CompanyType the form collects to the AccountInviteCompanyType
// enum member Account_Invite.company_type expects. Both enums share the same
// underlying string values but are distinct nominal types.
const ACCOUNT_INVITE_COMPANY_TYPE_BY_COMPANY_TYPE: Record<
  Exclude<CompanyType, CompanyType.SUPER>,
  AccountInviteCompanyType
> = {
  [CompanyType.BROKER]: AccountInviteCompanyType.BROKER,
  [CompanyType.CONTRACTOR]: AccountInviteCompanyType.CONTRACTOR,
  [CompanyType.HAULER]: AccountInviteCompanyType.HAULER,
  [CompanyType.OWNER_OPERATOR]: AccountInviteCompanyType.OWNER_OPERATOR,
  [CompanyType.PRODUCER]: AccountInviteCompanyType.PRODUCER,
};

export async function collectDetailsForNewConnectedAccount(
  ref: React.RefObject<AccountDetailsPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const address = AddressItem.deparseCreate(data.address as AddressItem);

  if (!address) {
    const error = new Error('[collectDetailsForNewConnectedAccount] missing address');
    Sentry.captureException(error);
    throw error;
  }

  const values: Pick<
    Account_Invite,
    'name' | 'address' | 'department_id' | 'external_id' | 'label_ids'
  > = {
    name: data.name,
    address,
    department_id: data.department?.value || null,
    external_id: data.externalId || null,
    label_ids: data.labels?.map((label) => label.id),
  };

  return { values, companyId: data.company.value };
}

export async function collectDetailsForNewNonConnectedAccount(
  ref: React.RefObject<AccountDetailsPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const address = AddressItem.deparseCreate(data.address as AddressItem);
  const billingAddress = data.isBillingAddressSame
    ? address
    : AddressItem.deparseCreate(data.billingAddress as AddressItem);

  const values: Pick<
    Account_Create,
    | 'name'
    | 'department_id'
    | 'address'
    | 'billing_address'
    | 'external_id'
    | 'accounting_id'
    | 'company_id'
    | 'label_ids'
  > = {
    name: data.name,
    department_id: data.department?.value || null,
    address,
    billing_address: billingAddress,
    external_id: data.externalId || null,
    accounting_id: data.accountingId || null,
    company_id: data.company.value,
    label_ids: data.labels?.map((label) => label.id),
  };

  return { values, companyId: data.company.value };
}

export async function collectContactsForNewConnectedAccount(
  ref: React.RefObject<ConnectedAccountContactPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const contact: Contact_Create = {
    name: `${data.primaryContact.firstName} ${data.primaryContact.lastName}`,
    email: data.primaryContact.email || undefined,
    phone: data.primaryContact.phone || undefined,
  };
  const isEmailInviteSelected = data.inviteTypes?.find(
    (option) => option.value === InviteType.EMAIL,
  );
  const isSmsInviteSelected = data.inviteTypes?.find(
    (option) => option.value === InviteType.SMS,
  );

  const values: Pick<Account_Invite, 'contact' | 'send_invite'> = {
    contact,
    send_invite: {
      email: Boolean(isEmailInviteSelected),
      sms: Boolean(isSmsInviteSelected),
    },
  };

  return values;
}

export async function collectContactsForNewNonConnectedAccount(
  ref: React.RefObject<AccountContactPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const primaryContact: Contact_Create = {
    name: data.primaryContact?.name || undefined,
    email: data.primaryContact?.email || undefined,
    phone: data.primaryContact?.phone || undefined,
  };
  const billingContact: Contact_Create = {
    name: data.billingContact?.name || undefined,
    email: data.billingContact?.email || undefined,
    phone: data.billingContact?.phone || undefined,
  };

  const values: Pick<Account_Create, 'primary_contact' | 'billing_contact'> = {
    primary_contact: primaryContact,
    billing_contact: data.isBillingContactSame ? primaryContact : billingContact,
  };

  return values;
}

export async function collectConfigurationForNewConnectedAccount(
  ref: React.RefObject<AccountConfigurationPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const companyType = data.companyType as {
    value: Exclude<CompanyType, CompanyType.SUPER>;
  } | null;

  const values: Pick<
    Account_Invite,
    'account_types' | 'company_type' | 'auto_accept_orders' | 'show_user_phone_number'
  > = {
    account_types: [AccountType.CUSTOMER],
    company_type: companyType?.value
      ? ACCOUNT_INVITE_COMPANY_TYPE_BY_COMPANY_TYPE[companyType.value]
      : undefined,
    auto_accept_orders:
      data.autoAcceptOrders.value === AutocompleteBooleanOptionValue.TRUE,
    show_user_phone_number: data.showUserPhoneNumber ?? true,
  };

  return values;
}

export async function collectConfigurationForNewNonConnectedAccount(
  ref: React.RefObject<AccountConfigurationPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const values: Pick<Account_Create, 'account_types' | 'auto_accept_orders'> = {
    account_types: [AccountType.CUSTOMER],
    auto_accept_orders:
      data.autoAcceptOrders.value === AutocompleteBooleanOptionValue.TRUE,
  };

  return values;
}

export async function collectNotesForConnectedAccount(
  ref: React.RefObject<NotesPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const values: Pick<Account_Invite, 'notes'> = {
    notes: data.notes?.trim() || null,
  };

  return values;
}

export async function collectNotesForNonConnectedAccount(
  ref: React.RefObject<NotesPanelHandler>,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const values: Pick<Account_Create, 'notes'> = {
    notes: data.notes?.trim() || null,
  };

  return values;
}

export async function createConnectedAccount(
  data: Account_Invite,
  targetCompanyId: string,
) {
  try {
    const response = await postV1CompaniesCompanyIdAccountsInvite({
      path: { 'company-id': targetCompanyId },
      body: data,
    });

    return { account: Account.parse(response.data.data) };
  } catch (error) {
    const isUnprocessableEntityError = isAxiosError(error) && error.status === 422;

    if (isUnprocessableEntityError) {
      const typedError = error as AxiosError<ModelError_Response>;

      return { errors: typedError.response?.data.error.errors ?? [] };
    }

    throw error;
  }
}

export async function createNonConnectedAccount(data: Account_Create, companyId: string) {
  try {
    const response = await postV1CompaniesCompanyIdAccounts({
      path: { 'company-id': companyId },
      body: data,
    });

    return { account: Account.parse(response.data.data) };
  } catch (error) {
    const isUnprocessableEntityError = isAxiosError(error) && error.status === 422;

    if (isUnprocessableEntityError) {
      const typedError = error as AxiosError<ModelError_Response>;

      return { errors: typedError.response?.data.error.errors ?? [] };
    }

    throw error;
  }
}

export async function createCompanyShares(
  ref: React.RefObject<AccountConfigurationPanelHandler>,
  account: Account,
) {
  const data = await ref.current?.onSubmit();

  if (!data) {
    return undefined;
  }

  const companyId = account.company?.id;
  const receiverCompanyId = account.connectedCompany?.id;

  if (!receiverCompanyId || !companyId) {
    return undefined;
  }

  const driverPromises = (data.drivers ?? []).map((driver) => {
    const sharedDriver: CompanyShare_Create = {
      receiver_company_id: receiverCompanyId,
      company_shareable_id: driver.value,
      company_shareable_type: CompanyShareableType.USER,
    };

    return postV1CompaniesCompanyIdCompanyShares({
      path: { 'company-id': companyId },
      body: sharedDriver,
    });
  });

  const equipmentPromises = (data.equipment ?? []).map((equipment) => {
    const sharedEquipment: CompanyShare_Create = {
      receiver_company_id: receiverCompanyId,
      company_shareable_id: equipment.value,
      company_shareable_type: CompanyShareableType.EQUIPMENT,
    };

    return postV1CompaniesCompanyIdCompanyShares({
      path: { 'company-id': companyId },
      body: sharedEquipment,
    });
  });

  const responses = await Promise.all(driverPromises.concat(equipmentPromises));

  const companyShares = responses.map((response) => {
    return CompanyShare.parse(response.data.data);
  });

  return companyShares;
}
